import React from 'react'

const Bar: React.FC<{ className: string }> = ({ className }) => (
  <div className={`animate-pulse rounded bg-zinc-200 ${className}`} />
)

type SkeletonVariant = 'about' | 'configuration' | 'how_it_works'

interface AppDetailSkeletonProps {
  variant?: SkeletonVariant
}

const PARA_1 = [
  ['w-[88%]', 'opacity-100'],
  ['w-[58%]', 'opacity-70'],
  ['w-[96%]', 'opacity-40'],
  ['w-[70%]', 'opacity-10']
] as const

const PARA_2 = [
  ['w-[72%]', 'opacity-100'],
  ['w-[50%]', 'opacity-70'],
  ['w-[84%]', 'opacity-40'],
  ['w-[40%]', 'opacity-10']
] as const

const AboutBody: React.FC = () => (
  <div className='flex min-w-0 flex-1 flex-col'>
    <div className='flex gap-3 overflow-hidden'>
      <Bar className='h-[200px] w-[360px] !rounded-xl' />
      <Bar className='h-[200px] w-[360px] !rounded-xl' />
    </div>
    <div className='flex w-full max-w-[90ch] flex-col gap-8 px-1.5 py-6'>
      <div className='flex flex-col gap-2'>
        {PARA_1.map(([width, opacity], i) => (
          <Bar key={i} className={`h-3 ${width} ${opacity}`} />
        ))}
      </div>
      <div className='flex flex-col gap-2'>
        {PARA_2.map(([width, opacity], i) => (
          <Bar key={i} className={`h-3 ${width} ${opacity}`} />
        ))}
      </div>
    </div>
  </div>
)

const ConfigurationBody: React.FC = () => (
  <div className='flex w-full justify-center'>
    <div className='flex w-full max-w-[640px] flex-col gap-4 py-3'>
      <Bar className='h-5 w-40' />
      <div className='flex flex-col gap-4 border-t border-zinc-200 pt-4'>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className='grid grid-cols-[180px_1fr] items-center gap-4'>
            <Bar className='h-3 w-24' />
            <Bar className='h-9 w-full !rounded-lg' />
          </div>
        ))}
      </div>
    </div>
  </div>
)

const AppDetailSkeleton: React.FC<AppDetailSkeletonProps> = ({ variant }) => {
  const resolved: SkeletonVariant =
    variant ??
    (typeof window === 'undefined'
      ? 'about'
      : (() => {
          const t = new URLSearchParams(window.location.search).get('tab')
          return t === 'configuration' || t === 'how_it_works' ? (t as SkeletonVariant) : 'about'
        })())

  return (
    <div className='flex min-h-0 w-full flex-1 flex-col gap-0 bg-zinc-50'>
      <div className='flex shrink-0 flex-col px-12 pt-4 pb-2'>
        <div className='flex items-center gap-2 py-2'>
          <Bar className='h-8 w-8 !rounded-lg' />
          <Bar className='h-8 w-8 !rounded-lg' />
          <Bar className='h-6 w-40' />
        </div>
      </div>

      <div className='relative min-h-0 flex-1'>
        <div className='h-full min-h-0 overflow-y-auto'>
          <div className='flex min-h-full flex-row items-stretch pb-6'>
            <div className='flex min-w-0 flex-1 justify-center px-12 min-xl:mt-4'>
              <div className='w-full max-w-[90ch]'>
                {resolved === 'configuration' ? <ConfigurationBody /> : <AboutBody />}
              </div>
            </div>

            <div className='flex w-[390px] flex-shrink-0'>
              <aside className='sticky top-0 flex h-fit min-w-0 flex-1 flex-col self-start px-10 py-6'>
                <Bar className='mb-2 h-16 w-16 !rounded-xl' />
                <div className='mb-6 flex flex-col gap-1'>
                  <Bar className='h-5 w-36' />
                  <Bar className='h-3 w-24' />
                </div>
                <Bar className='h-10 w-full !rounded-lg' />
                <div className='my-5 border-t border-zinc-200' />
                <div className='flex flex-col gap-3'>
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className='flex items-center gap-2 py-1'>
                      <Bar className='h-3 w-20 flex-shrink-0' />
                      <Bar className={`h-3 ${['w-28', 'w-24', 'w-20', 'w-16'][i]}`} />
                    </div>
                  ))}
                </div>
              </aside>
            </div>
          </div>
        </div>
        <div
          aria-hidden
          className='pointer-events-none absolute inset-y-0 right-[390px] w-px [background:linear-gradient(180deg,theme(colors.zinc.200/0)_0%,theme(colors.zinc.200)_10%)]'
        />
      </div>
    </div>
  )
}

export default AppDetailSkeleton
